import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { X, GraduationCap, Sparkles, Building2, ChevronRight, Trophy, Target, BookOpen, Briefcase } from 'lucide-react';

const QUIZ_QUESTIONS = [
  {
    question: "What's your biggest concern about studying abroad?",
    options: [
      { text: "Getting accepted", icon: Target, result: "ambitious" },
      { text: "Visa interview", icon: GraduationCap, result: "prepared" },
      { text: "Choosing the right major", icon: BookOpen, result: "explorer" },
      { text: "Career after graduation", icon: Briefcase, result: "strategic" }
    ]
  },
  {
    question: "How far along are you in the process?",
    options: [
      { text: "Just started researching", result: "early" },
      { text: "Shortlisting colleges", result: "mid" },
      { text: "Working on applications", result: "active" },
      { text: "Already applied, waiting", result: "late" }
    ]
  },
  {
    question: "What would help you most right now?",
    options: [
      { text: "Mock visa interview", result: "alec" },
      { text: "Know my admission chances", result: "wong" },
      { text: "Essay feedback", result: "wasif" },
      { text: "Career path clarity", result: "ashish" }
    ]
  }
];

const RESULTS = {
  alec: { tool: "ALEC", path: "/interview", tagline: "Your AI Visa Officer is ready", color: "from-teal-500 to-emerald-500" },
  wong: { tool: "WONG", path: "/admission-chances", tagline: "Let's predict your admission chances", color: "from-blue-500 to-indigo-500" },
  wasif: { tool: "Wasif", path: "/essay-review", tagline: "Get your essay reviewed by AI", color: "from-indigo-500 to-purple-500" },
  ashish: { tool: "Ashish", path: "/career-simulator", tagline: "Visualize your career path", color: "from-pink-500 to-purple-500" },
  faith: { tool: "Faith", path: "/academic-advisor", tagline: "Find the right major for you", color: "from-purple-500 to-indigo-500" }
};

const getRecommendation = (answers) => {
  // Last answer directly maps to a tool
  const lastAnswer = answers[2];
  if (lastAnswer && RESULTS[lastAnswer]) return RESULTS[lastAnswer];
  // Fallback
  if (answers[0] === 'explorer') return RESULTS.faith;
  if (answers[0] === 'prepared') return RESULTS.alec;
  if (answers[0] === 'ambitious') return RESULTS.wong;
  return RESULTS.ashish;
};

const WelcomePopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0); // 0=intro, 1-3=questions, 4=result
  const [answers, setAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const excludedPaths = ['/recommend', '/university', '/counselor', '/parent', '/login', '/signup', '/dashboard', '/settings'];
    const shouldExclude = excludedPaths.some(path => location.pathname.startsWith(path));
    if (shouldExclude) return;

    const hasSeenPopup = sessionStorage.getItem('wayo_welcome_popup_seen');
    if (!hasSeenPopup) {
      const timer = setTimeout(() => setIsOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem('wayo_welcome_popup_seen', 'true');
  };

  const handleStart = () => setStep(1);

  const handleAnswer = (result) => {
    setSelectedOption(result);
    setTimeout(() => {
      setAnswers([...answers, result]);
      setSelectedOption(null);
      if (step < 3) {
        setStep(step + 1);
      } else {
        setStep(4);
      }
    }, 300);
  };

  const recommendation = step === 4 ? getRecommendation(answers) : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in zoom-in-95 duration-300">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 hover:bg-white text-gray-600 hover:text-gray-900 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {/* INTRO */}
        {step === 0 && (
          <>
            <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 px-8 py-10 text-white text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4 animate-bounce">
                <Sparkles className="h-8 w-8" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Welcome to Wayo!</h2>
              <p className="text-white/90">Let's find the right AI tool for you</p>
            </div>
            <div className="px-8 py-6 text-center">
              <p className="text-gray-600 mb-6">Answer 3 quick questions and we'll match you with the perfect AI assistant for your journey.</p>
              <div className="flex items-center justify-center gap-2 mb-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="w-3 h-3 rounded-full bg-gray-200" />
                ))}
              </div>
              <div className="flex flex-col gap-3">
                <Button
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white py-6 text-lg"
                  onClick={handleStart}
                >
                  Let's Go <ChevronRight className="h-5 w-5 ml-1" />
                </Button>
                <button onClick={handleClose} className="text-sm text-gray-400 hover:text-gray-600">
                  Skip for now
                </button>
              </div>
            </div>
          </>
        )}

        {/* QUESTIONS */}
        {step >= 1 && step <= 3 && (
          <>
            <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 px-8 py-6 text-white">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-white/80">Question {step} of 3</p>
                <div className="flex gap-1.5">
                  {[1, 2, 3].map(i => (
                    <div
                      key={i}
                      className={`h-2 rounded-full transition-all duration-500 ${
                        i <= step ? 'w-8 bg-white' : 'w-3 bg-white/30'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <h3 className="text-xl font-bold">{QUIZ_QUESTIONS[step - 1].question}</h3>
            </div>
            <div className="px-6 py-5 space-y-3">
              {QUIZ_QUESTIONS[step - 1].options.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAnswer(opt.result)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-3 ${
                    selectedOption === opt.result
                      ? 'border-emerald-500 bg-emerald-50 scale-[0.98]'
                      : 'border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                    selectedOption === opt.result ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {String.fromCharCode(65 + idx)}
                  </div>
                  <span className="font-medium text-gray-700">{opt.text}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* RESULT */}
        {step === 4 && recommendation && (
          <>
            <div className={`bg-gradient-to-r ${recommendation.color} px-8 py-10 text-white text-center`}>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
                <Trophy className="h-8 w-8" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Your Perfect Match!</h2>
              <p className="text-white/90">{recommendation.tagline}</p>
            </div>
            <div className="px-8 py-6 text-center">
              <div className="bg-gray-50 rounded-xl p-6 mb-6 border border-gray-100">
                <p className="text-sm text-gray-500 mb-1">We recommend</p>
                <p className="text-2xl font-bold text-gray-900 mb-1">{recommendation.tool}</p>
                <p className="text-gray-600">{recommendation.tagline}</p>
              </div>
              <div className="flex flex-col gap-3">
                <Button
                  className={`w-full bg-gradient-to-r ${recommendation.color} text-white py-6 text-lg`}
                  onClick={() => { handleClose(); navigate(recommendation.path); }}
                >
                  Try {recommendation.tool} Now <ChevronRight className="h-5 w-5 ml-1" />
                </Button>
                <p className="text-sm text-gray-500 text-center px-2">
                  We're actively partnering with universities across the US. Can't find yours? Request it below.
                </p>
                <Button
                  variant="outline"
                  className="w-full py-5"
                  onClick={() => { handleClose(); navigate('/university-request'); }}
                >
                  <Building2 className="h-4 w-4 mr-2" />
                  Request a University
                </Button>
                <button onClick={handleClose} className="text-sm text-gray-400 hover:text-gray-600">
                  I'll explore on my own
                </button>
              </div>
            </div>
            <div className="bg-gray-50 px-8 py-4 text-center">
              <p className="text-xs text-gray-500">
                Already have an account? <Link to="/login" className="text-emerald-600 hover:underline" onClick={handleClose}>Log in</Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WelcomePopup;
